import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, LayoutGrid, List, ClipboardCheck, Package, CheckSquare, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { kompakt } from "@/pages/admin/budsjett/budsjettUtils";
import { SPONSOR_STAGES, SPONSOR_STAGE_LABEL, sponsorStageFarge } from "./eventosUtils";
import { ReadinessBadge } from "./Readiness";
import { BulkBar } from "./BulkBar";
import PartnerDrawer from "./PartnerDrawer";
import NyPartnerModal from "./NyPartnerModal";
import ProductsModal from "./ProductsModal";
import PartnerOutreachListe from "./PartnerOutreachListe";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const Kort = ({ p, onOpen, velgModus, valgt, onVelg }) => {
  const [dras, setDras] = useState(false);
  return (
    <button
      type="button"
      draggable={!velgModus}
      onDragStart={(e) => { e.dataTransfer.setData("text/partner", p.id); setDras(true); }}
      onDragEnd={() => setDras(false)}
      onClick={() => (velgModus ? onVelg(p.id) : onOpen(p.id))}
      className={`sak-kort w-full text-left bg-white border rounded-[10px] px-3 py-2.5 ${dras ? "dras" : ""} ${
        valgt ? "border-neutral-900 ring-1 ring-neutral-900" : "border-[#E7E4DB]"
      }`}
      data-testid={`partner-kort-${p.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-neutral-900 leading-snug">{p.company?.name}</p>
        {velgModus && (
          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
            valgt ? "bg-neutral-900 border-neutral-900 text-white" : "border-[#D8D4C8] text-transparent"
          }`} data-testid={`partnersos-velg-kort-${p.id}`}>
            <Check size={10} />
          </span>
        )}
      </div>
      <p className="text-[10.5px] text-neutral-400 mt-0.5 truncate">
        {[p.packageName, p.value ? `${kompakt(p.value)}` : null].filter(Boolean).join(" · ") || "—"}
      </p>
      <div className="flex items-center gap-2 mt-2">
        <ReadinessBadge computed={p.computed} />
        {p.published && <span className="w-1.5 h-1.5 rounded-full bg-[#F1E75A] border border-neutral-400 ml-auto" title="Publisert på nettsiden" />}
        {p.openTasks > 0 && <span className="text-[10px] text-neutral-400">{p.openTasks} saker</span>}
      </div>
    </button>
  );
};

export default function PartnersOS() {
  const [data, setData] = useState(null);
  const [visning, setVisning] = useState("pipeline");
  const [aapen, setAapen] = useState(null);
  const [nyModal, setNyModal] = useState(false);
  const [produkterModal, setProdukterModal] = useState(false);
  const [draOver, setDraOver] = useState(null);
  const [velgModus, setVelgModus] = useState(false);
  const [valgte, setValgte] = useState(() => new Set());
  const aktor = localStorage.getItem("nas_aktor") || "Admin";

  const velgToggle = (id) => setValgte((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/os/partners");
      setData(d);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    const p = new URLSearchParams(window.location.search).get("aapne");
    if (p && data.partners.some((a) => a.id === p)) {
      setAapen(p);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [data]);

  const patch = async (aid, p) => {
    try {
      const { data: r } = await api.patch(`/admin/os/partners/${aid}`, { ...p, actor: aktor });
      setData((prev) => ({
        ...prev,
        partners: prev.partners.map((x) => (x.id === aid ? { ...x, ...r.item, openTasks: x.openTasks } : x)),
      }));
    } catch (e) {
      feil(e);
    }
  };

  const opprett = async (payload) => {
    try {
      await api.post("/admin/os/partners", { ...payload, actor: aktor });
      setNyModal(false);
      toast.success("Partner lagt til");
      await load();
    } catch (e) {
      feil(e);
      throw e;
    }
  };

  const bulkPatch = async (ids, p) => {
    await Promise.all(ids.map((id) => patch(id, p)));
  };

  const bulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/admin/os/partners/${id}`)));
      setAapen(null);
      setData((prev) => ({ ...prev, partners: prev.partners.filter((x) => !ids.includes(x.id)) }));
      toast.success(`${ids.length} avtaler slettet`);
    } catch (e) {
      feil(e);
      await load();
    }
  };

  const bulkStadium = async (k) => {
    const ids = [...valgte];
    await bulkPatch(ids, { stage: k });
    toast.success(`${ids.length} flyttet til ${SPONSOR_STAGE_LABEL[k]}`);
    setValgte(new Set());
  };
  const bulkAnsvarlig = async (mid, navn) => {
    const ids = [...valgte];
    await bulkPatch(ids, { ownerId: mid });
    toast.success(mid ? `${ids.length} tildelt ${navn}` : `Ansvarlig fjernet på ${ids.length}`);
    setValgte(new Set());
  };
  const bulkSlettValgte = async () => {
    const ids = [...valgte];
    if (!window.confirm(`Slette ${ids.length} avtaler? Dette kan ikke angres.`)) return;
    await bulkDelete(ids);
    setValgte(new Set());
    setVelgModus(false);
  };

  const slett = async (aid) => {
    if (!window.confirm("Slette avtalen? Selskapet beholdes.")) return;
    try {
      await api.delete(`/admin/os/partners/${aid}`);
      setAapen(null);
      setData((prev) => ({ ...prev, partners: prev.partners.filter((x) => x.id !== aid) }));
      toast.success("Avtalen slettet");
    } catch (e) {
      feil(e);
    }
  };

  const aapenPartner = useMemo(() => data?.partners.find((p) => p.id === aapen) || null, [data, aapen]);

  if (!data) {
    return <p className="text-center text-xs text-neutral-400 py-20" data-testid="partnersos-laster">Laster partnere …</p>;
  }

  const signert = data.partners.filter((p) => p.stage === "signed");
  const publisert = data.partners.filter((p) => p.published).length;
  const signertVerdi = signert.reduce((s, p) => s + (p.value || 0), 0);
  const iPipeline = data.partners.filter((p) => p.stage !== "signed" && p.stage !== "declined").length;

  return (
    <div data-testid="partnersos-admin">
      <header className="flex flex-wrap items-end gap-3 mb-5">
        <div className="mr-auto">
          <p className="adm-eyebrow mb-1.5">Event 2027</p>
          <h1 className="adm-title">Partners</h1>
          <p className="adm-sub mt-1.5" data-testid="partnersos-teller">
            {signert.length} signert ({kompakt(signertVerdi)}) · {iPipeline} i pipeline · {publisert} på nettsiden
          </p>
        </div>
        <div className="flex bg-white border border-[#E7E4DB] rounded-lg p-0.5">
          {[
            { k: "pipeline", label: "Pipeline", Ico: LayoutGrid },
            { k: "liste", label: "Outreach", Ico: List },
            { k: "fulfillment", label: "Fulfillment", Ico: ClipboardCheck },
          ].map(({ k, label, Ico }) => (
            <button
              key={k}
              type="button"
              onClick={() => { setVisning(k); setVelgModus(false); setValgte(new Set()); }}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === k ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`partnersos-view-${k}`}
            >
              <Ico size={13} /> {label}
            </button>
          ))}
        </div>
        {visning === "pipeline" && (
          <button
            type="button"
            onClick={() => { setValgte(new Set()); setVelgModus((v) => !v); }}
            className={velgModus ? "adm-btn" : "adm-btn-ghost"}
            data-testid="partnersos-velg-btn"
          >
            <CheckSquare size={13} /> {velgModus ? "Avbryt" : "Velg"}
          </button>
        )}
        <button type="button" onClick={() => setProdukterModal(true)} className="adm-btn-ghost" data-testid="partnersos-produkter-btn">
          <Package size={14} /> Produkter
        </button>
        <button type="button" onClick={() => setNyModal(true)} className="adm-btn" data-testid="partnersos-ny-btn">
          <Plus size={14} /> Ny partner
        </button>
      </header>

      {visning === "pipeline" && (
        <div className="flex gap-2.5 overflow-x-auto pb-2 snap-x sak-noscroll h-[calc(100dvh-250px)] min-h-[420px]" data-testid="partnersos-pipeline">
          {SPONSOR_STAGES.map((st) => {
            const gruppe = data.partners.filter((p) => p.stage === st.k);
            const sum = gruppe.reduce((s, p) => s + (p.value || 0), 0);
            return (
              <div
                key={st.k}
                className={`flex-1 min-w-[230px] sm:min-w-[210px] shrink-0 snap-start rounded-xl bg-[#F1EFE8] flex flex-col transition-colors ${draOver === st.k ? "bg-[#ECEADE]" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDraOver(st.k); }}
                onDragLeave={() => setDraOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDraOver(null);
                  const id = e.dataTransfer.getData("text/partner");
                  const p = data.partners.find((x) => x.id === id);
                  if (p && p.stage !== st.k) patch(id, { stage: st.k });
                }}
                data-testid={`partnersos-kolonne-${st.k}`}
              >
                <div className="flex items-center gap-1.5 px-3.5 pt-2.5 pb-2 shrink-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: sponsorStageFarge(st.k) }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-600 truncate">{st.label}</span>
                  <span className="text-[10.5px] tabular-nums text-neutral-400 ml-auto">{gruppe.length}{sum > 0 ? ` · ${kompakt(sum)}` : ""}</span>
                </div>
                <div className="flex-1 overflow-y-auto sak-noscroll px-2 pb-2 flex flex-col gap-1.5">
                  {gruppe.map((p) => (
                    <Kort key={p.id} p={p} onOpen={setAapen} velgModus={velgModus} valgt={valgte.has(p.id)} onVelg={velgToggle} />
                  ))}
                  {gruppe.length === 0 && (
                    <div className="flex-1 min-h-[90px] m-0.5 border border-dashed border-[#E7E4DB] rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-neutral-300">Slipp kort her</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visning === "liste" && (
        <PartnerOutreachListe partners={data.partners} members={data.members} onOpen={setAapen} onPatch={patch} onBulkPatch={bulkPatch} onBulkDelete={bulkDelete} />
      )}

      {visning === "fulfillment" && (
        <div className="adm-card overflow-hidden" data-testid="partnersos-fulfillment">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Selskap</th>
                <th>Stadium</th>
                <th>Readiness</th>
                <th>Blockers</th>
                <th>Utestående</th>
                <th className="adm-right">Billetter</th>
              </tr>
            </thead>
            <tbody>
              {[...data.partners]
                .sort((a, b) => (b.computed?.blockers || 0) - (a.computed?.blockers || 0) || (a.computed?.completeness || 0) - (b.computed?.completeness || 0))
                .map((p) => {
                  const utestaende = (p.computed?.items || []).filter((i) => i.state === "open" && !i.blocker).map((i) => i.label);
                  const q = p.ticketQuota || {};
                  return (
                    <tr key={p.id} onClick={() => setAapen(p.id)} className="cursor-pointer" data-testid={`partnersos-fulfillment-rad-${p.id}`}>
                      <td className="font-medium">{p.company?.name}</td>
                      <td className="text-[11.5px]" style={{ color: sponsorStageFarge(p.stage) }}>{SPONSOR_STAGE_LABEL[p.stage]}</td>
                      <td><ReadinessBadge computed={p.computed} size="md" /></td>
                      <td className="text-[12px] text-red-600">{(p.computed?.blockerLabels || []).join(", ") || "—"}</td>
                      <td className="text-[12px] text-neutral-500">{utestaende.join(", ") || "—"}</td>
                      <td className="text-right tabular-nums text-neutral-500">{q.total ? `${q.used || 0}/${q.total}` : "—"}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {visning === "pipeline" && velgModus && (
        <BulkBar
          antall={valgte.size}
          alleValgt={valgte.size === data.partners.length}
          onVelgAlle={() => setValgte(valgte.size === data.partners.length ? new Set() : new Set(data.partners.map((p) => p.id)))}
          stages={SPONSOR_STAGES}
          stageFarge={sponsorStageFarge}
          stageLabel={SPONSOR_STAGE_LABEL}
          members={data.members}
          onStadium={bulkStadium}
          onAnsvarlig={bulkAnsvarlig}
          onSlett={bulkSlettValgte}
          onLukk={() => { setVelgModus(false); setValgte(new Set()); }}
          t="ppipeline"
        />
      )}

      {aapenPartner && (
        <PartnerDrawer
          partner={aapenPartner}
          members={data.members}
          aktor={aktor}
          onPatch={(p) => patch(aapenPartner.id, p)}
          onDelete={() => slett(aapenPartner.id)}
          onLukk={() => setAapen(null)}
        />
      )}
      {nyModal && (
        <NyPartnerModal companies={data.companies} onLagre={opprett} onLukk={() => setNyModal(false)} />
      )}
      {produkterModal && <ProductsModal onLukk={() => setProdukterModal(false)} />}
    </div>
  );
}
